import type { Msg, Session } from '@halo/chat-sdk-react';
import {
  ItemExpireContext as MessageExpireContext,
  MessageImage,
  MessageMetaRoot,
} from '@halo/ui-chat-react';
import { Center } from '@chakra-ui/react';
import { useActionsStore } from '@/store/actions';
import { isForwardMessage, useStaticAppStore } from '@/store/client';
import { Emoji } from '@/components/Emoji';
import {
  MsgAvatar,
  MsgBody,
  MsgBubble,
  MsgExpireAnimate,
  MsgFooter,
  MsgFooterReplies,
  MsgForwardFrom,
  MsgHeader,
  MsgItem,
  MsgMenuTrigger,
  MsgReadStatus,
  MsgReply,
} from '../../msg-parts';

// Message
export interface MsgImageProps {
  session: Session;
  message: Msg;
}

export const MsgImage = memo(function MsgImage({ session, message }: MsgImageProps) {
  const appStore = useStaticAppStore();
  const sessionStore = appStore.sessionStores[session.chat_id];

  const onAbort = useCallback(() => {
    sessionStore.getState().cancelFile(message.cli_msg_id);
  }, [sessionStore, message]);

  const imagePreviewRef = useActionsStore((state) => state.imagePreviewRef);

  const handleImageClick = useCallback(() => {
    const sessionStoreInstance = appStore.sessionStores[session.chat_id];
    const client = appStore.client;
    const user = appStore.user;
    imagePreviewRef?.current?.open(message, { sessionStoreInstance, client, user });
  }, [
    appStore.sessionStores,
    appStore.client,
    appStore.user,
    imagePreviewRef,
    message,
    session.chat_id,
  ]);

  const isForward = useMemo(() => isForwardMessage(message.msg_action), [message.msg_action]);

  const bubbleProps = useMemo(() => (isForward ? undefined : { p: '0' }), [isForward]);
  const metaProps = useMemo(
    () => (isForward ? undefined : { pb: '14px', px: '16px' }),
    [isForward]
  );

  return (
    <MsgItem message={message}>
      <MessageExpireContext message={message}>
        <MsgAvatar message={message} session={session} />
        <MsgBody message={message}>
          <MsgHeader message={message} session={session} />
          <MsgMenuTrigger message={message}>
            <MsgExpireAnimate session={session} message={message}>
              <MsgBubble message={message} {...bubbleProps}>
                {message.msg_ext?.forwardUid && (
                  <MsgForwardFrom forwardUid={message.msg_ext.forwardUid} />
                )}
                {message.link_msg_id && (
                  <MsgReply session={session} link_msg_id={message.link_msg_id} />
                )}
                <MessageImage
                  onImageClick={handleImageClick}
                  message={message}
                  onAbort={onAbort}
                  sessionStore={sessionStore}
                  client={appStore.client}
                ></MessageImage>
                <MessageMetaRoot pb="1" {...metaProps}>
                  <Emoji.MessageMeta chat_id={session.chat_id} message={message} />
                  <Center marginLeft="auto" gap="1.5">
                    <MsgReadStatus message={message} session={session} />
                  </Center>
                </MessageMetaRoot>
              </MsgBubble>
            </MsgExpireAnimate>
          </MsgMenuTrigger>
          <MsgFooter message={message}>
            <MsgFooterReplies message={message} />
          </MsgFooter>
        </MsgBody>
      </MessageExpireContext>
    </MsgItem>
  );
});
